import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { MapPin, RefreshCw, Navigation, Clock, User, Activity, Eye } from 'lucide-react';
import { getDashboardStats, getWorkerLocationLogs } from '../../../../models/mmsModel';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom numbered marker icons
const createNumberedIcon = (number, isFirst = false, isLast = false) => {
  const color = isFirst ? '#10b981' : isLast ? '#ef4444' : '#3b82f6';
  const bgColor = isFirst ? '#d1fae5' : isLast ? '#fee2e2' : '#dbeafe';
  
  return L.divIcon({
    html: `<div style="
      background-color: ${color};
      width: 28px;
      height: 28px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 12px;
      color: white;
    ">${number}</div>`,
    className: 'custom-numbered-marker',
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
};

const LocationsList = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDashboardStats();
      console.log('Dashboard response:', response);
      
      // Handle ApiController wrapper pattern - data might be in response.data
      const data = response?.data || response;
      
      if (data && data.workers_locations) {
        setLocations(data.workers_locations);
      } else {
        setLocations([]);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load worker locations');
      setLocations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchLocations, 30000);
    return () => clearInterval(interval);
  }, [fetchLocations]);

  const handleViewHistory = async (worker) => {
    setSelectedWorker(worker);
    setShowHistoryModal(true);
    try {
      // Get location history for the last 7 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 7);
      
      const response = await getWorkerLocationLogs(worker.worker_id, {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      });
      
      console.log('Location history response:', response);
      
      // Handle ApiController wrapper pattern - data might be in response.data.data or response.data
      const data = response?.data?.data || response?.data || response;
      
      if (data && Array.isArray(data)) {
        setLocationHistory(data);
      } else {
        setLocationHistory([]);
      }
    } catch (err) {
      toast.error('Failed to load location history');
      setLocationHistory([]);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diff = Math.floor((now - date) / 1000); // difference in seconds

      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
      
      return date.toLocaleDateString('en-ZA', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (timestamp) => {
    if (!timestamp) return 'gray';
    const diff = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (diff < 300) return 'green'; // Active (< 5 min)
    if (diff < 3600) return 'yellow'; // Recent (< 1 hour)
    return 'red'; // Stale (> 1 hour)
  };

  const openGoogleMaps = (latitude, longitude) => {
    if (latitude && longitude) {
      window.open(`https://www.google.com/maps?q=${latitude},${longitude}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <MapPin className="h-5 w-5" style={{ color: '#0087D4' }} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Worker Locations</h1>
                <p className="text-xs text-gray-500 mb-1">
                  Real-time tracking of artisans
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                    {locations.length} worker{locations.length !== 1 ? 's' : ''} tracked
                  </span>
                </div>
              </div>
            </div>
            
            {/* Refresh Button */}
            <button
              onClick={fetchLocations}
              disabled={loading}
              className="flex-shrink-0 inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} style={{ color: loading ? '#0096DE' : 'currentColor' }} />
            </button>
          </div>
        </div>

        {/* Locations Grid */}
        {loading && locations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <div className="flex justify-center items-center">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-600">Loading locations...</span>
            </div>
          </div>
        ) : locations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <MapPin className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">No Location Data</h2>
            <p className="text-gray-500">
              No workers have reported their location yet. Location data will appear here once workers log in to the mobile app and start updating their location.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {locations.map((worker) => {
              const statusColor = getStatusColor(worker.last_location_time);
              const hasValidLocation = worker.latitude && worker.longitude;
              
              return (
                <div key={worker.worker_id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                  {/* Worker Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                          {worker.worker_name?.charAt(0) || 'W'}
                        </div>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-semibold text-gray-800">{worker.worker_name}</h3>
                        <p className="text-xs text-gray-500">{worker.worker_email}</p>
                      </div>
                    </div>
                    
                    {/* Status Indicator */}
                    <div className="flex items-center">
                      <span 
                        className={`inline-block w-2.5 h-2.5 rounded-full ${
                          statusColor === 'green' ? 'bg-green-500' : 
                          statusColor === 'yellow' ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        title={
                          statusColor === 'green' ? 'Active' : 
                          statusColor === 'yellow' ? 'Recent' : 'Stale'
                        }
                      />
                    </div>
                  </div>

                  {/* Location Info */}
                  <div className="space-y-2 mb-3">
                    {hasValidLocation ? (
                      <>
                        <div className="flex items-start text-xs text-gray-600">
                          <Navigation className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0 text-blue-500" />
                          <span className="font-mono">{worker.latitude?.toFixed(6)}, {worker.longitude?.toFixed(6)}</span>
                        </div>
                        {worker.accuracy && (
                          <div className="flex items-start text-xs text-gray-600">
                            <Activity className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0 text-green-500" />
                            <span>Accuracy: ±{Math.round(worker.accuracy)}m</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex items-start text-xs text-gray-400">
                        <MapPin className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
                        <span>No GPS coordinates available</span>
                      </div>
                    )}
                    
                    <div className="flex items-start text-xs text-gray-600">
                      <Clock className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0 text-gray-400" />
                      <span>{formatDate(worker.last_location_time)}</span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pt-3 border-t border-gray-100">
                    <button
                      onClick={() => handleViewHistory(worker)}
                      className="flex-1 inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      History
                    </button>
                    
                    {hasValidLocation && (
                      <button
                        onClick={() => openGoogleMaps(worker.latitude, worker.longitude)}
                        className="flex-1 inline-flex items-center justify-center px-3 py-1.5 rounded-md text-xs font-medium text-white hover:opacity-90 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        style={{ backgroundColor: '#0087D4' }}
                      >
                        <MapPin className="h-3.5 w-3.5 mr-1.5" />
                        View Map
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Location History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Location History</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedWorker?.worker_name} - Last 7 days ({locationHistory.length} points)
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowHistoryModal(false);
                    setSelectedWorker(null);
                    setLocationHistory([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            <div className="overflow-y-auto" style={{ height: 'calc(90vh - 100px)' }}>
              {locationHistory.length === 0 ? (
                <div className="flex items-center justify-center h-full p-8">
                  <div className="text-center py-8">
                    <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-600 font-medium mb-2">No location history available</p>
                    <p className="text-sm text-gray-500">
                      This worker hasn't reported location updates during the selected period (last 7 days).
                    </p>
                    
                    {/* Show current location if available */}
                    {selectedWorker?.latitude && selectedWorker?.longitude && (
                      <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-900 mb-2">Current Location:</p>
                        <div className="flex items-center justify-center gap-2 text-xs text-blue-700">
                          <Navigation className="h-3.5 w-3.5" />
                          <span className="font-mono">
                            {selectedWorker.latitude.toFixed(6)}, {selectedWorker.longitude.toFixed(6)}
                          </span>
                          <button
                            onClick={() => openGoogleMaps(selectedWorker.latitude, selectedWorker.longitude)}
                            className="ml-2 text-blue-600 hover:text-blue-800 underline"
                          >
                            View on Map
                          </button>
                        </div>
                        <p className="text-xs text-blue-600 mt-1">
                          Last updated: {formatDate(selectedWorker.last_location_time)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-0 h-full">
                  {/* Map Section - 2/3 width */}
                  <div className="col-span-2 h-full relative">
                    <MapContainer
                      center={[
                        parseFloat(locationHistory[0].latitude),
                        parseFloat(locationHistory[0].longitude)
                      ]}
                      zoom={13}
                      style={{ height: '100%', width: '100%' }}
                      className="z-0"
                    >
                      <TileLayer
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      />
                      
                      {/* Draw path line */}
                      <Polyline
                        positions={locationHistory.map(log => [
                          parseFloat(log.latitude),
                          parseFloat(log.longitude)
                        ])}
                        pathOptions={{
                          color: '#3b82f6',
                          weight: 3,
                          opacity: 0.6,
                          dashArray: '10, 10'
                        }}
                      />
                      
                      {/* Draw markers for each point */}
                      {locationHistory.map((log, index) => {
                        const isFirst = index === 0;
                        const isLast = index === locationHistory.length - 1;
                        
                        return (
                          <Marker
                            key={index}
                            position={[parseFloat(log.latitude), parseFloat(log.longitude)]}
                            icon={createNumberedIcon(locationHistory.length - index, isFirst, isLast)}
                          >
                            <Popup>
                              <div className="p-2 min-w-[200px]">
                                <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                                  <div className={`w-3 h-3 rounded-full ${
                                    isFirst ? 'bg-green-500' : isLast ? 'bg-red-500' : 'bg-blue-500'
                                  }`} />
                                  <h4 className="font-bold text-gray-900">
                                    {isFirst ? 'Latest' : isLast ? 'Oldest' : `Point ${locationHistory.length - index}`}
                                  </h4>
                                </div>
                                
                                <div className="space-y-1 text-xs text-gray-700 mb-3">
                                  <div className="flex justify-between">
                                    <span className="font-medium">Coordinates:</span>
                                    <span className="text-blue-600 font-mono">
                                      {parseFloat(log.latitude).toFixed(5)}, {parseFloat(log.longitude).toFixed(5)}
                                    </span>
                                  </div>
                                  {log.accuracy && (
                                    <div className="flex justify-between">
                                      <span className="font-medium">Accuracy:</span>
                                      <span>±{Math.round(log.accuracy)}m</span>
                                    </div>
                                  )}
                                  <div className="flex justify-between">
                                    <span className="font-medium">Time:</span>
                                    <span className="font-medium">{formatDate(log.timestamp)}</span>
                                  </div>
                                </div>

                                <a
                                  href={`https://www.google.com/maps?q=${log.latitude},${log.longitude}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded text-center transition-colors"
                                >
                                  Open in Google Maps →
                                </a>
                              </div>
                            </Popup>
                          </Marker>
                        );
                      })}
                    </MapContainer>
                    
                    {/* Legend overlay */}
                    <div className="absolute top-4 right-4 z-[1000] bg-white rounded-lg shadow-lg border border-gray-200 p-3">
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-green-500"></div>
                          <span className="font-medium">Latest Position</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                          <span>Historical Points</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 rounded-full bg-red-500"></div>
                          <span className="font-medium">Oldest Position</span>
                        </div>
                        <div className="pt-2 border-t border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-0 border-t-2 border-dashed border-blue-500"></div>
                            <span>Movement Path</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* List Section - 1/3 width */}
                  <div className="col-span-1 border-l border-gray-200 overflow-y-auto bg-gray-50">
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        Timeline
                      </h3>
                      <div className="space-y-2">
                        {locationHistory.map((log, index) => {
                          const logDate = new Date(log.timestamp);
                          const isToday = logDate.toDateString() === new Date().toDateString();
                          const isFirst = index === 0;
                          const isLast = index === locationHistory.length - 1;
                          
                          return (
                            <div 
                              key={index} 
                              className="p-2 bg-white rounded border border-gray-200 hover:border-blue-300 hover:shadow-sm transition-all cursor-pointer"
                            >
                              <div className="flex items-start gap-2">
                                <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 mt-0.5 ${
                                  isFirst ? 'bg-green-500' : isLast ? 'bg-red-500' : 'bg-blue-500'
                                }`}>
                                  {locationHistory.length - index}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-xs font-medium text-gray-900 mb-1">
                                    {formatDate(log.timestamp)}
                                  </div>
                                  <div className="text-[11px] text-gray-500 font-mono truncate">
                                    {parseFloat(log.latitude).toFixed(4)}, {parseFloat(log.longitude).toFixed(4)}
                                  </div>
                                  {log.accuracy && (
                                    <div className="text-[10px] text-gray-400 mt-0.5">
                                      ±{Math.round(log.accuracy)}m
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationsList;
